import { Tuple2 }		from "ts-base/tuple";
import { Validated }	from "ts-base/validated";
import { Nea }			from "ts-base/nea";

import { Validator }	from "ts-base/validation/validator";

/** takes an unvalidated query string and returns validated data - or why validation failed */
export type Schema<T>	= Validator<Input, T>;

/** un-validated input value */
export type Input		= ReadonlyArray<NamedParam>;

/** validated output value, or why validation failed */
export type Output<X>	= Validator.Output<X>;

/** a query string consists of an arbitrary number of these key/value pairs */
export type NamedParam		= Tuple2<string, ParameterInput>;

/** takes an unvalidated query parameter and returns validated data - or why validation failed */
export type ParameterSchema<T>	= Validator<ParameterInput, T>;

/** un-validated input value for a single parameter */
export type ParameterInput		= string;

/** validated output value for a single parameter, or why validation failed */
export type ParameterOutput<X>	= Validator.Output<X>;

//-----------------------------------------------------------------------------
//## combinators

/** accepts all input and returns void unconditionally */
export const irrelevant:Schema<void>	= Validator.constant(void(0));

/** builds a validator for an object from an object of validators for each field of the output object */
export const objectOf		= <T extends Record<string, unknown>>(spec:Validator.SelfRecord<ReadonlyArray<ParameterInput>, T>):Schema<Readonly<T>> =>
	Validator.fields<Input, ReadonlyArray<ParameterInput>, T>(
		spec,
		(key) => (params) =>
			Validated.valid(
				params.filter(([ k, _v ]) => k === key).map(([ _k, v ]) => v)
			)
	);

/** a query parameter can exist or be missing, but must not be given more than once */
export const optional	= <S, T>(base:Validator<S, T>):Validator<ReadonlyArray<S>, T|null>	=>
	Validated.andThen(zeroOrOne(), Validator.nullable(base));

/** a query parameter must exist, and cannot be given more than once */
export const one	= <S, T>(base:Validator<S, T>):Validator<ReadonlyArray<S>, T>	=>
	Validated.andThen(exactlyOne(), base);

/** a query parameter must exist at least once */
export const oneOrMore	= <S, T>(base:Validator<S, T>):Validator<ReadonlyArray<S>, Nea<T>>	=>
	Validated.andThen(Validator.array(base), Validator.nonEmpty());

/** a query parameter can exist an arbitrary number of times, including zero */
export const many	= <S, T>(base:Validator<S, T>):Validator<ReadonlyArray<S>, ReadonlyArray<T>>	=>
	Validator.array(base);

const exactlyOne	= <T>():Validator<ReadonlyArray<T>, T> =>
	(input:ReadonlyArray<T>) => input.length === 1 ? Validated.valid(input[0]) : Validated.invalid1(`expected exactly one item, got ${input.length}`);

const zeroOrOne	= <T>():Validator<ReadonlyArray<T>, T|null> =>
	(input:ReadonlyArray<T>) => input.length === 1 ? Validated.valid(input[0]) : input.length === 0 ? Validated.valid(null) : Validated.invalid1(`expected zero or one item, got ${input.length}`);

//-----------------------------------------------------------------------------
//## json types

export const string:ParameterSchema<string>		= Validator.identity();

export const enumValue	= <T extends string>(values:ReadonlyArray<T>):ParameterSchema<T>	=>
	Validated.andThen(
		string,
		Validator.enumValue(values)
	);

export const enumValueNamed	= <T extends string>(name:string, values:ReadonlyArray<T>):ParameterSchema<T>	=>
	Validated.andThen(
		string,
		Validator.enumValueNamed(name, values)
	);
